/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  LegendConnect,
  OlMapViewConnect,
  TimeSliderConnect,
} from '@opengeoweb/core';
import type {
  mapTypes,
  CoreAppStore,
  layerTypes,
  syncGroupsTypes,
} from '@opengeoweb/store';
import {
  layerUtils,
  mapSelectors,
  defaultLayers,
  syncGroupsActions,
  syncConstants,
  layerActions,
  mapActions,
  uiActions,
  uiTypes,
} from '@opengeoweb/store';
import { webmapUtils } from '@opengeoweb/webmap';
import type {
  DrawModeExitCallback,
  DrawModeValue,
  OpenLayersLayerProps,
} from '@opengeoweb/webmap-react';
import {
  genericOpenLayersFeatureStyle,
  OpenLayersFeatureLayer,
  OpenLayersMapDraw,
} from '@opengeoweb/webmap-react';
import { srsAndBboxDefault } from './constants';

type MapViewGeoJsonLayer = OpenLayersLayerProps & {
  selectedFeatureIds?: string[];
};

export interface MapViewGeoJsonProps {
  baseLayers?: layerTypes.Layer[];
  projection?: mapTypes.MapPreset['proj'];
  geoJSONLayers: MapViewGeoJsonLayer[];
  children?: React.ReactNode;
}

const MapViewGeoJson: React.FC<MapViewGeoJsonProps> = ({
  baseLayers: initialBaseLayers,
  projection: initialProjection = srsAndBboxDefault,
  geoJSONLayers = [],
  children,
}: MapViewGeoJsonProps) => {
  const dispatch = useDispatch();
  const mapWindowRef = React.useRef(null);

  const firstMap = useSelector(
    (store: CoreAppStore) => mapSelectors.getFirstMap(store),
    shallowEqual,
  );
  const firstMapId = firstMap?.id ? firstMap.id : '';

  const mapLayers = useSelector(
    (store: CoreAppStore) => mapSelectors.getMapLayers(store, firstMapId),
    shallowEqual,
  );
  const mapDimensions = useSelector(
    (store: CoreAppStore) => mapSelectors.getMapDimensions(store, firstMapId),
    shallowEqual,
  );

  const mapId = React.useRef(`sigmet-${webmapUtils.generateMapId()}`).current;
  const timesliderId = React.useRef(
    `sigmet-${webmapUtils.generateTimesliderId()}`,
  ).current;

  const syncGroupAddGroup = React.useCallback(
    (payload: syncGroupsTypes.SyncGroupAddGroupPayload): void => {
      dispatch(syncGroupsActions.syncGroupAddGroup(payload));
    },
    [dispatch],
  );

  const syncGroupAddTarget = React.useCallback(
    (payload: syncGroupsTypes.SyncGroupAddTargetPayload): void => {
      dispatch(syncGroupsActions.syncGroupAddTarget(payload));
    },
    [dispatch],
  );

  React.useEffect(() => {
    if (mapLayers?.length) {
      // use maplayers, but change layerId and current mapId
      const layers = mapLayers.map((layer: layerTypes.Layer) => ({
        ...layer,
        id: layer.id || webmapUtils.generateLayerId(),
        mapId,
      }));
      dispatch(
        layerActions.setLayers({
          mapId,
          layers,
        }),
      );
    }

    if (mapDimensions?.length) {
      dispatch(
        mapActions.mapChangeDimension({
          mapId,
          dimension: mapDimensions[0],
          origin: 'MapViewGeoJson',
        }),
      );
    }

    const baseLayers = initialBaseLayers || [
      { ...defaultLayers.overLayer, id: `countryborders-${mapId}` },
    ];

    dispatch(
      layerActions.setBaseLayers({
        mapId,
        layers: baseLayers,
      }),
    );

    dispatch(
      mapActions.setBbox({
        mapId,
        bbox: initialProjection.bbox,
        srs: initialProjection.srs,
      }),
    );
    syncGroupAddGroup({
      groupId: 'Time_SigmetAirmet',
      title: 'Group 1 for sigmet/airmet map',
      type: syncConstants.SYNCGROUPS_TYPE_SETTIME,
    });

    syncGroupAddTarget({ groupId: 'Time_SigmetAirmet', targetId: mapId });
    syncGroupAddTarget({
      groupId: 'Time_SigmetAirmet',
      targetId: timesliderId,
    });

    dispatch(
      uiActions.setActiveMapIdForDialog({
        type: uiTypes.DialogTypes.LayerManager,
        mapId,
        source: 'module',
      }),
    );

    return (): void => {
      dispatch(
        uiActions.setActiveMapIdForDialog({
          type: uiTypes.DialogTypes.LayerManager,
          mapId,
          source: 'app',
        }),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="MapView"
      ref={mapWindowRef}
      style={{ position: 'relative', height: '100%' }}
    >
      <LegendConnect mapId={mapId} />
      <OlMapViewConnect
        controls={{
          mapControlsPositionTop: 0,
          layerManagerAndLegend: true,
          zoomControls: true,
          dimensionSelect: true,
        }}
        mapId={mapId}
        source="module"
      >
        {children}
        {geoJSONLayers.map((layer) => (
          <div data-testid={`${layer.id}`} key={layer.id}>
            <OpenLayersFeatureLayer
              featureCollection={
                !layer.isInEditMode ? layer.geojson : undefined
              }
              style={genericOpenLayersFeatureStyle}
              zIndex={2000}
            />
            <OpenLayersMapDraw
              drawMode={layer.drawMode as DrawModeValue}
              exitDrawModeCallback={(reason: DrawModeExitCallback): void => {
                try {
                  layer?.exitDrawModeCallback?.(reason);
                } catch (e) {
                  window.console.trace(e);
                }
              }}
              geojson={layer.geojson!}
              isInEditMode={layer.isInEditMode || false}
              selectedFeatureIndex={
                layerUtils.getSelectedFeatureIndexByIds(
                  layer.geojson,
                  layer.selectedFeatureIds,
                ) || 0
              }
              updateGeojson={layer.updateGeojson}
              viewOnlyStyle={genericOpenLayersFeatureStyle}
            />
          </div>
        ))}
      </OlMapViewConnect>
      <div
        style={{
          position: 'absolute',
          left: '0',
          bottom: '0',
          zIndex: 1000,
          width: '100%',
        }}
      >
        <TimeSliderConnect
          mapId={mapId}
          mapWindowRef={mapWindowRef}
          sourceId={timesliderId}
        />
      </div>
    </div>
  );
};

export default MapViewGeoJson;
